import { useState, useEffect, useCallback, useMemo } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import axios from "axios";
import {
  Users, Search, Filter, Loader2, Clock, CheckCircle, XCircle,
  Phone, Mail, Building2, Monitor, MoreHorizontal
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_CONFIG = {
  trial: { label: "Prueba Activa", color: "bg-blue-500/10 text-blue-400 border-blue-500/20" },
  contacted: { label: "Contactado", color: "bg-yellow-500/10 text-yellow-400 border-yellow-500/20" },
  converted: { label: "Convertido", color: "bg-green-500/10 text-green-400 border-green-500/20" },
  expired: { label: "Expirado", color: "bg-red-500/10 text-red-400 border-red-500/20" },
};

const SYSTEM_LABELS = {
  loteria: "Loteria",
  envios: "Envios/Shipping",
  taller: "Taller Mecanico",
  prestamos: "Prestamos",
  contabilidad: "Contabilidad",
  erp: "ERP",
  crm: "CRM",
};

const TrialsView = ({ auth }) => {
  const [trials, setTrials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editStatus, setEditStatus] = useState("");
  const [editNotes, setEditNotes] = useState("");

  const authConfig = useMemo(() => ({ auth: { username: auth.username, password: auth.password } }), [auth.username, auth.password]);

  const fetchTrials = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/trials`, authConfig);
      setTrials(res.data.trials || []);
    } catch {
      toast.error("Error cargando registros");
    } finally {
      setLoading(false);
    }
  }, [authConfig]);

  useEffect(() => { fetchTrials(); }, [fetchTrials]);

  const filtered = trials.filter((t) => {
    const matchSearch = !search || 
      t.name?.toLowerCase().includes(search.toLowerCase()) ||
      t.email?.toLowerCase().includes(search.toLowerCase()) ||
      t.business?.toLowerCase().includes(search.toLowerCase()) ||
      t.phone?.toLowerCase().includes(search.toLowerCase());
    const matchStatus = !statusFilter || t.status === statusFilter;
    return matchSearch && matchStatus;
  });

  const handleUpdate = async (trialId) => {
    try {
      const payload = {};
      if (editStatus) payload.status = editStatus;
      if (editNotes !== undefined) payload.notes = editNotes;
      await axios.put(`${API}/admin/trials/${trialId}`, payload, authConfig);
      toast.success("Registro actualizado");
      setEditingId(null);
      fetchTrials();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error actualizando");
    }
  };

  const statusCounts = trials.reduce((acc, t) => {
    acc[t.status || "trial"] = (acc[t.status || "trial"] || 0) + 1;
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 text-[#3b82f6] animate-spin" />
      </div>
    );
  }

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-white mb-1">Pruebas Gratis</h1>
        <p className="text-[#94a3b8] text-sm">{trials.length} registros de prueba gratuita</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="glass-card p-4 text-center">
          <p className="text-2xl font-bold text-white">{trials.length}</p>
          <p className="text-[#94a3b8] text-xs">Total</p>
        </div>
        <div className="glass-card p-4 text-center">
          <p className="text-2xl font-bold text-blue-400">{statusCounts.trial || 0}</p>
          <p className="text-[#94a3b8] text-xs">Activas</p>
        </div>
        <div className="glass-card p-4 text-center">
          <p className="text-2xl font-bold text-yellow-400">{statusCounts.contacted || 0}</p>
          <p className="text-[#94a3b8] text-xs">Contactados</p>
        </div>
        <div className="glass-card p-4 text-center">
          <p className="text-2xl font-bold text-green-400">{statusCounts.converted || 0}</p>
          <p className="text-[#94a3b8] text-xs">Convertidos</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94a3b8]" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por nombre, email, empresa, telefono..."
            className="w-full bg-[#0b1221] border border-white/10 pl-10 pr-4 py-2.5 text-white text-sm placeholder:text-[#94a3b8]/50 focus:border-[#3b82f6] focus:outline-none"
            data-testid="trials-search"
          />
        </div>
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={() => setStatusFilter("")}
            className={`px-3 py-2 text-xs font-medium border transition-colors ${!statusFilter ? "bg-[#3b82f6]/10 border-[#3b82f6]/30 text-[#3b82f6]" : "border-white/10 text-[#94a3b8]"}`}
          >
            Todos
          </button>
          {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
            <button
              key={key}
              onClick={() => setStatusFilter(key)}
              className={`px-3 py-2 text-xs font-medium border transition-colors ${statusFilter === key ? cfg.color + " border" : "border-white/10 text-[#94a3b8]"}`}
              data-testid={`filter-status-${key}`}
            >
              {cfg.label}
            </button>
          ))}
        </div>
      </div>

      {/* Trials List */}
      <div className="space-y-3" data-testid="trials-list">
        {filtered.length > 0 ? filtered.map((trial, i) => {
          const st = STATUS_CONFIG[trial.status] || STATUS_CONFIG.trial;
          const isEditing = editingId === trial.id;

          return (
            <motion.div
              key={trial.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.03 }}
              className="glass-card p-4"
              data-testid={`trial-item-${i}`}
            >
              <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                <div className="flex items-start gap-3 min-w-0">
                  <div className="w-10 h-10 bg-[#3b82f6]/10 flex items-center justify-center text-[#3b82f6] font-bold text-sm flex-shrink-0">
                    {trial.name?.charAt(0)?.toUpperCase() || "?"}
                  </div>
                  <div className="min-w-0">
                    <p className="text-white font-medium text-sm">{trial.name || "Sin nombre"}</p>
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1">
                      {trial.email && (
                        <span className="flex items-center gap-1 text-[#94a3b8] text-xs">
                          <Mail className="w-3 h-3" /> {trial.email}
                        </span>
                      )}
                      {trial.phone && (
                        <span className="flex items-center gap-1 text-[#94a3b8] text-xs">
                          <Phone className="w-3 h-3" /> {trial.phone}
                        </span>
                      )}
                      {trial.business && (
                        <span className="flex items-center gap-1 text-[#94a3b8] text-xs">
                          <Building2 className="w-3 h-3" /> {trial.business}
                        </span>
                      )}
                      {trial.system && (
                        <span className="flex items-center gap-1 text-[#94a3b8] text-xs">
                          <Monitor className="w-3 h-3" /> {SYSTEM_LABELS[trial.system] || trial.system}
                        </span>
                      )}
                    </div>
                    {trial.notes && !isEditing && (
                      <p className="text-[#94a3b8] text-xs mt-1.5 italic">Nota: {trial.notes}</p>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-2 flex-shrink-0">
                  <span className={`text-xs px-2 py-1 font-medium border ${st.color}`}>
                    {st.label}
                  </span>
                  <span className="text-[#94a3b8] text-[10px] flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {trial.created_at ? new Date(trial.created_at).toLocaleDateString("es", { day: "numeric", month: "short", year: "numeric" }) : ""}
                  </span>
                  <button
                    onClick={() => {
                      if (isEditing) { setEditingId(null); }
                      else { setEditingId(trial.id); setEditStatus(trial.status || "trial"); setEditNotes(trial.notes || ""); }
                    }}
                    className="text-[#94a3b8] hover:text-white p-1"
                    data-testid={`trial-edit-${i}`}
                  >
                    <MoreHorizontal className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Edit panel */}
              {isEditing && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  className="mt-3 pt-3 border-t border-white/5 flex flex-col sm:flex-row gap-3"
                >
                  <div className="flex-1">
                    <label className="block text-[#94a3b8] text-[10px] mb-1">Estado</label>
                    <select
                      value={editStatus}
                      onChange={(e) => setEditStatus(e.target.value)}
                      className="w-full bg-[#02040a] border border-white/10 px-3 py-2 text-white text-sm focus:border-[#3b82f6] focus:outline-none"
                      data-testid={`trial-status-select-${i}`}
                    >
                      {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
                        <option key={key} value={key}>{cfg.label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="flex-1">
                    <label className="block text-[#94a3b8] text-[10px] mb-1">Notas</label>
                    <input
                      value={editNotes}
                      onChange={(e) => setEditNotes(e.target.value)}
                      placeholder="Agregar nota..."
                      className="w-full bg-[#02040a] border border-white/10 px-3 py-2 text-white text-sm focus:border-[#3b82f6] focus:outline-none"
                      data-testid={`trial-notes-${i}`}
                    />
                  </div>
                  <div className="flex items-end gap-2">
                    <button
                      onClick={() => handleUpdate(trial.id)}
                      className="bg-[#3b82f6] text-white text-sm px-4 py-2 font-medium"
                      data-testid={`trial-save-${i}`}
                    >
                      Guardar
                    </button>
                    <button
                      onClick={() => setEditingId(null)}
                      className="border border-white/10 text-[#94a3b8] text-sm px-4 py-2"
                    >
                      Cancelar
                    </button>
                  </div>
                </motion.div>
              )}
            </motion.div>
          );
        }) : (
          <div className="glass-card p-12 text-center">
            <Users className="w-12 h-12 text-[#3b82f6]/20 mx-auto mb-3" />
            <p className="text-[#94a3b8]">
              {search || statusFilter ? "No se encontraron resultados" : "Sin registros de prueba gratis aun"}
            </p>
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default TrialsView;
